import type { FC } from "react";
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { questionnaireFieldValidation } from "src/state/QuestionnaireCubit/QuestionnaireCubit";
import Loader from "src/ui/components/Loader/Loader";
import Translate from "src/ui/components/Translate/Translate";
import { z } from "zod";

import {
  AutoForm,
  AutoFormDataField,
  AutoFormDataFieldDate,
  brioche,
  Button,
  DataFieldGroup,
  pickerCurrentTime,
  useAutoFormControls
} from "@9amhealth/shared";
import {
  getLocalTimeZone,
  now as internationalizedNow
} from "@internationalized/date";
import { Blac } from "blac-next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { inchesToCm } from "src/lib/unitConversion";
import { validateDate } from "src/lib/zod/validateDate";
import LabResultsCubit from "src/state/LabResultsCubit/LabResultsCubit";
import { TrackEvent, TrackType } from "src/state/Track/TrackCubit";
import { tracker } from "src/state/state";
import type { TranslationKey } from "src/types/translationKey";
import ErrorBox from "src/ui/components/StyledComponents/ErrorBox";
import Track from "src/ui/components/Track/Track";
import DateTimePicker from "../DateTimePicker/DateTimePicker";
import styled from "@emotion/styled";
import MediaPlayer from "../MediaPlayer/MediaPlayer";
import { VimeoVideoId } from "src/constants/vimeoIds";
import { IconCheckmarkWhite } from "src/constants/icons";
import { LoincCodingCode } from "src/constants/fhir";
import { LinkButton } from "atom/button/LinkButton";
import { dismissBrioche } from "src/lib/dismissBrioche";

const Description = styled.p`
  color: var(--Greys-Charcoal80, rgba(33, 33, 33, 0.8));
`;

const VideoWrapper = styled.div`
  margin-bottom: 3rem;
`;
const WaistCircumferenceSchema = z.object({
  date: validateDate,
  waistCircumference: z.string().refine(
    (v) => {
      if (!v) {
        return false;
      }
      const regex = questionnaireFieldValidation.waistCircumference;
      return regex.test(v);
    },
    {
      message: translate("upload_labs.waist_circumference.invalid_input_error")
    }
  )
});

type FormValues = z.infer<typeof WaistCircumferenceSchema>;

const WaistCircumferenceForm: FC<{
  onComplete: () => void;
}> = (props) => {
  const [errorMessage, setErrorMessage] = React.useState<TranslationKey>();
  const navigate = useNavigate();

  const initialFormValues: FormValues = {
    waistCircumference: "",
    date: internationalizedNow(getLocalTimeZone())
  };

  const handleSubmit = useCallback(
    async (formValues: FormValues): Promise<void> => {
      let error;

      try {
        const waistCircumferenceInCm = inchesToCm(
          Number(formValues.waistCircumference)
        );

        const [dateTimeZone] = formValues.date.toString().split("[");
        const observation = LabResultsCubit.createObservationWaistCircumference(
          waistCircumferenceInCm.toString(),
          dateTimeZone
        );

        error = await LabResultsCubit.storeSelfReportedLabValues(
          [observation],
          "messenger"
        );

        const isOnDatahubPage =
          window.location.pathname.includes("/lab-results");

        if (isOnDatahubPage) {
          brioche.success({
            content: (
              <Translate msg={"upload_labs.waist_circumference.success"} />
            ),
            icon: <IconCheckmarkWhite />,
            dismissFn: dismissBrioche
          });
        } else {
          brioche.success({
            content: (
              <Translate msg={"upload_labs.waist_circumference.success"} />
            ),
            icon: <IconCheckmarkWhite />,
            controls: (
              <LinkButton
                onClick={() => {
                  navigate(
                    `/app/lab-results?type=${LoincCodingCode.waistCircumference}`
                  );
                }}
              >
                <Translate msg={"view"} />
              </LinkButton>
            ),
            dismissFn: dismissBrioche
          });
        }

        tracker.track(TrackEvent.WaistCircumferenceForm, {
          type: TrackType.complete
        });
        void Blac.getBloc(LabResultsCubit).loadObservations();
      } catch (observationError: unknown) {
        reportErrorSentry(observationError);
        error = (observationError as Error).message;
      }

      if (error) {
        setErrorMessage("error_generic");
      } else {
        props.onComplete();
      }
    },
    [props.onComplete]
  );

  const autoFormControls = useAutoFormControls({
    schema: WaistCircumferenceSchema,
    initialValue: initialFormValues,
    onSubmit: (data) => void handleSubmit(data as unknown as FormValues)
  });

  const [showPicker, setShowPicker] = useState(false);

  const handleFocusChange = () => {
    const isCoarse = matchMedia("(pointer:coarse)").matches;

    if (!isCoarse) {
      return;
    }
    setShowPicker(true);
  };

  return (
    <div>
      <div className="center">
        <VideoWrapper>
          <MediaPlayer
            addRadius={true}
            videoId={VimeoVideoId.WAIST_CIRCUMFERENCE_GUIDE}
            videoProvider="vimeo"
            title=""
            playIcon="simple"
            inline
            options={{
              controls: true
            }}
          />
        </VideoWrapper>

        <h3 className="as-h4">
          <Translate msg="upload_labs.waist_circumference.title" />
        </h3>
      </div>

      <Description className="center as-body">
        <Translate msg="upload_labs.waist_circumference.description" />
      </Description>

      <Track event={TrackEvent.WaistCircumferenceForm} type={TrackType.open} />

      <DateTimePicker
        show={showPicker}
        onFinish={() => {
          setShowPicker(false);
        }}
        onChange={(dateTime) => {
          autoFormControls.setValue("date", dateTime);
        }}
        initialDate={autoFormControls.getValues().date}
        title={translate("upload_labs.glucose.date_input_label")}
        granularity="minute"
      />

      <nine-spacer s="md"></nine-spacer>

      <AutoForm {...autoFormControls.props}>
        <DataFieldGroup>
          <AutoFormDataFieldDate
            onFocus={handleFocusChange}
            maxValue={pickerCurrentTime()}
            label={translate(
              "upload_labs.waist_circumference.date_input_label"
            )}
            name="date"
            granularity="minute"
            hideTimeZone
          />

          <AutoFormDataField
            inputMode="decimal"
            name="waistCircumference"
            mask="00[0].0"
            label={translate(
              "upload_labs.waist_circumference.value_input_label"
            )}
            affix="inch"
          />
        </DataFieldGroup>

        <nine-spacer s="lg" />

        <Button type="submit" theme="sunrise" style={{ margin: "auto" }}>
          {translate("confirm")}
        </Button>
      </AutoForm>

      {errorMessage && (
        <>
          <nine-spacer s="sm"></nine-spacer>
          <ErrorBox data-severity="error">
            <Translate msg={errorMessage} fallback="error_generic" />
          </ErrorBox>
        </>
      )}

      <Loader fixed absolute background overContent />
    </div>
  );
};

export default WaistCircumferenceForm;
